import React, { useCallback, useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send, CheckCircle2, MessageSquare, Loader2, Lock, X } from 'lucide-react';
import { recruitmentAdminApi } from '@/lib/recruitmentApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { RecruitmentStepper } from '@/components/recruitment/RecruitmentStepper';
import { CandidateRosterCard } from '@/components/recruitment/CandidateRosterCard';
import { VacancyProfileCard } from '@/components/recruitment/VacancyProfileCard';
import { CandidatePoolPanel } from '@/components/recruitment/CandidatePoolPanel';
import ActivityTimelinePanel from '@/components/recruitment/ActivityTimelinePanel';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(getUiLocale()); } catch { return iso; }
}

export default function AdminRecruitmentDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [proc, setProc] = useState(null);
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [closeReason, setCloseReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProc(await recruitmentAdminApi.get(id));
    } catch (e) {
      toast.error(t('recruitment.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!proc?.vacancy_request_id) { setVacancy(null); return; }
    api.get(`/admin/vacancy-requests/${proc.vacancy_request_id}`)
      .then((r) => setVacancy(r.data))
      .catch(() => setVacancy(null));
  }, [proc?.vacancy_request_id]);

  const run = async (fn, successKey) => {
    setBusy(true);
    try {
      await fn();
      if (successKey) toast.success(t(successKey));
      await load();
    } catch (e) {
      toast.error(t('toasts.actionFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const onSendIntake = () => run(() => recruitmentAdminApi.sendIntake(id), 'recruitment.actions.sentIntake');
  const onApprove   = () => run(() => recruitmentAdminApi.approve(id),    'recruitment.actions.approved');
  const onApproveProfile = () => run(
    () => api.post(`/admin/recruitment-processes/${id}/profile/approve-from-vacancy`),
    'recruit2.profile.approved',
  );
  const onAddNote = async () => {
    if (!note.trim()) return;
    await run(async () => {
      await recruitmentAdminApi.addNote(id, note.trim());
      setNote('');
    }, 'recruitment.actions.noteAdded');
  };
  const onClose = () => run(
    () => recruitmentAdminApi.close(id, closeReason.trim() || null),
    'recruitment.actions.closed',
  );

  if (!proc) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      <Topbar
        title={`${proc.position_title}`}
        subtitle={`${proc.company_name}${proc.department ? ' · ' + proc.department : ''}`}
        actions={
          <Link to="/admin/recruitment">
            <Button size="sm" variant="outline" className="gap-1.5">
              <ArrowLeft className="h-4 w-4" /> {t('common.back')}
            </Button>
          </Link>
        }
      />
      <div className="p-6 lg:p-8 space-y-5">
        <Card className="p-5 shadow-card" data-testid="recruitment-detail">
          <RecruitmentStepper currentStage={proc.stage} stageHistory={proc.stage_history} />
          <div className="mt-5 flex flex-wrap gap-2 pt-4 border-t border-border/60">
            {/* Actions appear based on the current stage */}
            {proc.stage === 1 && proc.vacancy_request_id && (
              <Button onClick={onApproveProfile} disabled={busy} className="gap-1.5 bg-success hover:bg-success/90" data-testid="btn-approve-profile">
                <CheckCircle2 className="h-4 w-4" /> {t('recruit2.profile.approveProfile', { defaultValue: 'Aprobar Perfil' })}
              </Button>
            )}
            {proc.stage === 1 && (
              <Button onClick={onSendIntake} disabled={busy || !!proc.intake_sent_at}
                variant={proc.vacancy_request_id ? 'outline' : 'default'}
                className="gap-1.5" data-testid="btn-send-intake">
                <Send className="h-4 w-4" />
                {proc.intake_sent_at
                  ? t('recruitment.actions.sentToClient')
                  : proc.vacancy_request_id
                    ? t('recruit2.profile.requestMoreInfo', { defaultValue: 'Solicitar información adicional' })
                    : t('recruitment.actions.sendIntake')}
              </Button>
            )}
            {proc.stage === 2 && (
              <Button onClick={onApprove} disabled={busy} className="gap-1.5 bg-success hover:bg-success/90" data-testid="btn-approve-process">
                <CheckCircle2 className="h-4 w-4" /> {t('recruitment.actions.approve')}
              </Button>
            )}
            {proc.stage !== 11 && (
              <div className="ml-auto flex items-center gap-2">
                <Input
                  className="w-48"
                  placeholder={t('recruitment.actions.closeReasonPh')}
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                />
                <Button variant="outline" onClick={onClose} disabled={busy} data-testid="btn-close-process">
                  <X className="h-4 w-4 mr-1.5" /> {t('recruitment.actions.close')}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Workflow Improvement #001 — vacancy data as Position Profile v1 */}
        {vacancy && <VacancyProfileCard vacancy={vacancy} />}
        {proc.candidate_snapshot && (proc.candidate_snapshot.name || proc.candidate_snapshot.email) && (
          <Card className="p-5 shadow-card border-blue-200/60 bg-blue-50/30" data-testid="recruitment-candidate-snapshot">
            <h3 className="font-semibold mb-1">
              {t('recruitment.candidateSnapshot.title', { defaultValue: 'Candidate submitted with the request' })}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {t('recruitment.candidateSnapshot.hint', { defaultValue: 'Provided by the client at request time — prefilled automatically in "Add candidates".' })}
            </p>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[
                ['name', proc.candidate_snapshot.name],
                ['email', proc.candidate_snapshot.email],
                ['phone', proc.candidate_snapshot.phone],
              ].filter(([, v]) => v).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-muted-foreground">{t(`recruitment.candidateSnapshot.${k}`, { defaultValue: k })}</dt>
                  <dd className="font-medium" data-testid={`candidate-snapshot-${k}`}>{String(v)}</dd>
                </React.Fragment>
              ))}
              {proc.candidate_snapshot.cv_url && (
                <>
                  <dt className="text-muted-foreground">{t('recruitment.candidateSnapshot.cv', { defaultValue: 'CV' })}</dt>
                  <dd><a href={proc.candidate_snapshot.cv_url} target="_blank" rel="noreferrer" className="text-primary underline" data-testid="candidate-snapshot-cv">{t('recruitment.candidateSnapshot.openCv', { defaultValue: 'Open CV link' })}</a></dd>
                </>
              )}
            </dl>
          </Card>
        )}

        {/* Intake form snapshot */}
        {proc.intake_form && (
          <Card className="p-5 shadow-card" data-testid="recruitment-intake-snapshot">
            <h3 className="font-semibold mb-3">{t('recruitment.intake.title')}</h3>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {Object.entries({
                positionTitle: proc.intake_form.position_title,
                department: proc.intake_form.department,
                keyResponsibilities: proc.intake_form.key_responsibilities,
                salaryRange: proc.intake_form.salary_range,
                yearsExperience: proc.intake_form.years_experience,
                educationLevel: proc.intake_form.education_level,
                certifications: proc.intake_form.certifications,
                urgency: proc.intake_form.urgency,
                workModality: proc.intake_form.work_modality,
                reportingLine: proc.intake_form.reporting_line,
                numberOfOpenings: proc.intake_form.number_of_openings,
              }).filter(([, v]) => v !== null && v !== '' && v !== undefined).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-muted-foreground">{t(`recruitment.intake.fields.${k}`, { defaultValue: k })}</dt>
                  <dd className="font-medium">{String(v)}</dd>
                </React.Fragment>
              ))}
              {(proc.intake_form.required_competencies || []).length > 0 && (
                <>
                  <dt className="text-muted-foreground">{t('recruitment.intake.fields.requiredCompetencies')}</dt>
                  <dd className="font-medium">{proc.intake_form.required_competencies.join(', ')}</dd>
                </>
              )}
              {(proc.intake_form.soft_skills || []).length > 0 && (
                <>
                  <dt className="text-muted-foreground">{t('recruitment.intake.fields.softSkills')}</dt>
                  <dd className="font-medium">{proc.intake_form.soft_skills.join(', ')}</dd>
                </>
              )}
              {(proc.intake_form.languages || []).length > 0 && (
                <>
                  <dt className="text-muted-foreground">{t('recruitment.intake.fields.languages')}</dt>
                  <dd className="font-medium">{proc.intake_form.languages.map((l) => t(`recruitment.intake.languageOpts.${l}`)).join(', ')}</dd>
                </>
              )}
            </dl>
          </Card>
        )}

        {/* Workflow Improvement #001 — STEP 1 candidates, STEP 2 assessments */}
        {proc.stage >= 3 && (
          <CandidatePoolPanel
            processId={id}
            companyId={proc.company_id}
            stage={proc.stage}
            prefillCandidate={proc.candidate_snapshot || null}
            onChanged={load}
          />
        )}

        {/* Internal notes (advisor only) */}
        <Card className="p-5 shadow-card" data-testid="recruitment-notes">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-semibold">{t('recruitment.notes.title')}</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">{t('recruitment.notes.subtitle')}</p>
          <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
            {(proc.notes || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('recruitment.notes.empty')}</p>
            ) : (proc.notes || []).map((n) => (
              <div key={n.id} className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <p className="text-sm leading-relaxed">{n.body}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {n.author_name} · {fmtDate(n.created_at)}
                </p>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Textarea
              data-testid="note-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('recruitment.notes.placeholder')}
            />
            <Button onClick={onAddNote} disabled={busy || !note.trim()} className="gap-1.5" data-testid="btn-add-note">
              <MessageSquare className="h-4 w-4" /> {t('recruitment.actions.addNote')}
            </Button>
          </div>
        </Card>

        {/* Candidates list (Sprint A · G-1 — named roster) */}
        <CandidateRosterCard processId={id} variant="admin" companyId={proc.company_id} />

        {/* Sprint TC-D.1 — read-only activity timeline */}
        <ActivityTimelinePanel processId={id} />
      </div>
    </>
  );
}
